import React from "react";
import { Box, Typography, Alert, AlertTitle } from "@mui/material";
import { Warning, Build, Error, Info } from "@mui/icons-material";
import { SystemBanner } from "Network/Core/SystemBanners/systemBanners";

interface HeaderBannerProps {
  banner: SystemBanner;
}

const getPriorityStyles = (priority: string) => {
  switch (priority) {
    case "critical":
      return { severity: "error", bgcolor: "#ffebee", color: "#c62828" };
    case "high":
      return { severity: "warning", bgcolor: "#fff3e0", color: "#ef6c00" };
    case "medium":
      return { severity: "info", bgcolor: "#e3f2fd", color: "#1565c0" };
    case "low":
      return { severity: "success", bgcolor: "#e8f5e8", color: "#2e7d32" };
    default:
      return { severity: "info", bgcolor: "#e3f2fd", color: "#1565c0" };
  }
};

const getTypeIcon = (type: string) => {
  switch (type?.toUpperCase()) {
    case "DISASTER_ALERT":
      return <Warning sx={{ fontSize: 28 }} />;
    case "MAINTENANCE":
      return <Build sx={{ fontSize: 28 }} />;
    case "SYSTEM_ISSUE":
      return <Error sx={{ fontSize: 28 }} />;
    case "GENERAL":
      return <Info sx={{ fontSize: 28 }} />;
    default:
      return <Info sx={{ fontSize: 28 }} />;
  }
};

const HeaderBanner: React.FC<HeaderBannerProps> = ({ banner }) => {
  // Add null check for banner
  if (!banner) {
    return null;
  }

  const priorityStyles = getPriorityStyles(banner.priority || 'low');
  const typeIcon = getTypeIcon(banner.type || 'GENERAL');

  const handleDetailClick = () => {
    if (banner.detailUrl) {
      window.location.href = banner.detailUrl;
    }
  };

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1200,
        width: "100%",
        mb: 2
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1200,
          width: "100%",
          mb: 2,
          display: "flex",
          backgroundColor: priorityStyles.bgcolor,
          color: priorityStyles.color,
          cursor: banner.detailUrl ? "pointer" : "default"
        }}
        onClick={banner.detailUrl ? handleDetailClick : undefined}
      >
        {/* Icon Section */}
        <Box
          sx={{
            width: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100%"
          }}
        >
          {typeIcon}
        </Box>

        {/* Content Section */}
        <Box sx={{ flex: 1, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            {banner.title || 'System Banner'}
          </Typography>
          {banner.bannerContent ? (
            <Box
              dangerouslySetInnerHTML={{
                __html: banner.bannerContent,
              }}
            />
          ) : (
            <Typography variant="body2">
              {banner.message || ''}
            </Typography>
          )}
          {banner.detailUrl && (
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                fontWeight: 500,
                textDecoration: "underline",
                color: "inherit"
              }}
            >
              Click for more details
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default HeaderBanner;